/**
 * FolderSidebar
 *
 * Navigation sidebar showing system folders with unread counts, custom folders,
 * labels with colored dots, and compose button.
 */

import {
  Inbox,
  Send,
  FileText,
  Trash2,
  AlertOctagon,
  Archive,
  Tag,
  ChevronDown,
  ChevronRight,
  Clock,
  Shield,
  CalendarClock,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ComposeButton } from './ComposeButton';
import type { EmailFolder, EmailLabel, EmailAccount } from '@/lib/api';

const SYSTEM_FOLDER_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  inbox: Inbox,
  sent: Send,
  drafts: FileText,
  trash: Trash2,
  spam: AlertOctagon,
  archive: Archive,
};

const SYSTEM_FOLDER_ORDER = ['inbox', 'sent', 'drafts', 'archive', 'spam', 'trash'];

interface FolderSidebarProps {
  folders: EmailFolder[];
  labels: EmailLabel[];
  activeFolderId: string | null;
  activeLabelId: string | null;
  accountId?: string;
  activeAccount?: EmailAccount | null;
  accounts?: EmailAccount[];
  onSelectAccount?: (accountId: string) => void;
  snoozedCount?: number;
  scheduledCount?: number;
  screenerPendingCount?: number;
  schedulingEnabled?: boolean;
  screenerEnabled?: boolean;
  onSelectFolder: (folderId: string) => void;
  onSelectLabel: (labelId: string) => void;
  onNavigate: (path: string) => void;
}

export function FolderSidebar({
  folders,
  labels,
  activeFolderId,
  activeLabelId,
  accountId,
  activeAccount,
  accounts = [],
  onSelectAccount,
  snoozedCount = 0,
  scheduledCount = 0,
  screenerPendingCount = 0,
  schedulingEnabled = false,
  screenerEnabled = false,
  onSelectFolder,
  onSelectLabel,
  onNavigate,
}: FolderSidebarProps) {
  const [labelsExpanded, setLabelsExpanded] = useState(true);

  // Split system vs custom folders
  const systemFolders = folders
    .filter((f) => f.folderType === 'system' && f.systemRole)
    .sort((a, b) => {
      const aIdx = SYSTEM_FOLDER_ORDER.indexOf(a.systemRole || '');
      const bIdx = SYSTEM_FOLDER_ORDER.indexOf(b.systemRole || '');
      return (aIdx === -1 ? 99 : aIdx) - (bIdx === -1 ? 99 : bIdx);
    });
  const customFolders = folders.filter((f) => f.folderType === 'custom');

  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const activeAccounts = accounts.filter((a) => a.status === 'active');
  const hasMultipleAccounts = activeAccounts.length > 1;

  return (
    <div className="flex flex-col h-full py-2">
      {/* Active account */}
      {activeAccount && (
        <div className="px-3 mb-2">
          <button
            className={cn(
              'flex items-center gap-2 w-full px-2 py-1.5 rounded-md text-left transition-colors',
              hasMultipleAccounts ? 'hover:bg-muted cursor-pointer' : 'cursor-default',
            )}
            onClick={() => hasMultipleAccounts && setAccountMenuOpen(!accountMenuOpen)}
          >
            <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
              {(activeAccount.displayName || activeAccount.emailAddress).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">
                {activeAccount.displayName || activeAccount.emailAddress.split('@')[0]}
              </div>
              <div className="text-xs text-muted-foreground truncate">
                {activeAccount.emailAddress}
              </div>
            </div>
            {hasMultipleAccounts && (
              <ChevronDown
                className={cn(
                  'h-3 w-3 text-muted-foreground transition-transform',
                  accountMenuOpen && 'rotate-180',
                )}
              />
            )}
          </button>

          {/* Account switcher dropdown */}
          {accountMenuOpen && hasMultipleAccounts && (
            <div className="mt-1 border border-border rounded-md bg-popover shadow-md">
              {activeAccounts
                .filter((a) => a.id !== activeAccount.id)
                .map((a) => (
                  <button
                    key={a.id}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-left hover:bg-muted transition-colors text-sm"
                    onClick={() => {
                      onSelectAccount?.(a.id);
                      setAccountMenuOpen(false);
                    }}
                  >
                    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium flex-shrink-0">
                      {(a.displayName || a.emailAddress).charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="truncate">
                        {a.displayName || a.emailAddress.split('@')[0]}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{a.emailAddress}</div>
                    </div>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Compose button */}
      <div className="px-3 mb-3">
        <ComposeButton accountId={accountId} />
      </div>

      {/* System folders */}
      <nav className="flex flex-col gap-0.5 px-2">
        {systemFolders.map((folder) => {
          const Icon = SYSTEM_FOLDER_ICONS[folder.systemRole || ''] || Inbox;
          const isActive = folder.id === activeFolderId && !activeLabelId;
          return (
            <button
              key={folder.id}
              onClick={() => onSelectFolder(folder.id)}
              className={cn(
                'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm w-full text-left transition-colors',
                'hover:bg-muted',
                isActive && 'bg-muted font-medium',
              )}
            >
              <Icon className="h-4 w-4 flex-shrink-0" />
              <span className="flex-1 truncate capitalize">{folder.name}</span>
              {folder.unreadCount > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs px-1.5 py-0 h-5 min-w-[20px] justify-center"
                >
                  {folder.unreadCount}
                </Badge>
              )}
            </button>
          );
        })}
      </nav>

      {/* Virtual folders */}
      <div className="flex flex-col gap-0.5 px-2 mt-2 pt-2 border-t border-border">
        {schedulingEnabled && (
          <>
            <button
              onClick={() => onNavigate('snoozed')}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm w-full text-left hover:bg-muted transition-colors"
            >
              <Clock className="h-4 w-4" />
              <span className="flex-1">Snoozed</span>
              {snoozedCount > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs px-1.5 py-0 h-5 min-w-[20px] justify-center"
                >
                  {snoozedCount}
                </Badge>
              )}
            </button>
            <button
              onClick={() => onNavigate('scheduled')}
              className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm w-full text-left hover:bg-muted transition-colors"
            >
              <CalendarClock className="h-4 w-4" />
              <span className="flex-1">Scheduled</span>
              {scheduledCount > 0 && (
                <Badge
                  variant="secondary"
                  className="text-xs px-1.5 py-0 h-5 min-w-[20px] justify-center"
                >
                  {scheduledCount}
                </Badge>
              )}
            </button>
          </>
        )}
        {screenerEnabled && (
          <button
            onClick={() => onNavigate('screener')}
            className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm w-full text-left hover:bg-muted transition-colors"
          >
            <Shield className="h-4 w-4" />
            <span className="flex-1">Screener</span>
            {screenerPendingCount > 0 && (
              <Badge className="text-xs px-1.5 py-0 h-5 min-w-[20px] justify-center">
                {screenerPendingCount}
              </Badge>
            )}
          </button>
        )}
      </div>

      {/* Custom folders */}
      {customFolders.length > 0 && (
        <div className="flex flex-col gap-0.5 px-2 mt-2 pt-2 border-t border-border">
          <span className="text-xs font-medium text-muted-foreground px-2 py-1">Folders</span>
          {customFolders.map((folder) => {
            const isActive = folder.id === activeFolderId && !activeLabelId;
            return (
              <button
                key={folder.id}
                onClick={() => onSelectFolder(folder.id)}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm w-full text-left hover:bg-muted transition-colors',
                  isActive && 'bg-muted font-medium',
                )}
              >
                <Inbox className="h-4 w-4 flex-shrink-0" />
                <span className="flex-1 truncate">{folder.name}</span>
                {folder.unreadCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="text-xs px-1.5 py-0 h-5 min-w-[20px] justify-center"
                  >
                    {folder.unreadCount}
                  </Badge>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Labels */}
      <div className="flex flex-col gap-0.5 px-2 mt-2 pt-2 border-t border-border">
        <button
          onClick={() => setLabelsExpanded(!labelsExpanded)}
          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
        >
          {labelsExpanded ? (
            <ChevronDown className="h-3 w-3" />
          ) : (
            <ChevronRight className="h-3 w-3" />
          )}
          <Tag className="h-3 w-3" />
          <span>Labels</span>
        </button>
        {labelsExpanded &&
          labels.map((label) => {
            const isActive = label.id === activeLabelId;
            return (
              <button
                key={label.id}
                onClick={() => onSelectLabel(label.id)}
                className={cn(
                  'flex items-center gap-2 px-2 py-1.5 rounded-md text-sm w-full text-left hover:bg-muted transition-colors',
                  isActive && 'bg-muted font-medium',
                )}
              >
                <span
                  className="h-2.5 w-2.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: label.color }}
                />
                <span className="flex-1 truncate">{label.name}</span>
                {label.messageCount > 0 && (
                  <span className="text-xs text-muted-foreground">{label.messageCount}</span>
                )}
              </button>
            );
          })}
      </div>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Settings link */}
      <div className="px-2 mt-2 pt-2 border-t border-border">
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start text-xs"
          onClick={() => onNavigate('settings')}
        >
          Settings
        </Button>
      </div>
    </div>
  );
}
